/**
 * A moldura dos três apps: portão de sessão, cliente de dados, e o que fazer quando não há sessão.
 *
 * Os três a usam para não haver três versões de "está logado?", "o que faço num 503" e "quanto tempo
 * o cache vive". Cada app passa a sua identidade e o que é dele.
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, useState, type ReactNode } from "react";
import { falhaDe, valeRepetir } from "./api.js";
import { PortaoDeSessao, consumirMarcaDeSaida, type EstadoDaSessao } from "./sessao.js";
import { Botao, EstadoCarregando, EstadoErroRecuperavel } from "./estados.js";
import { textos } from "./textos.js";
import { aplicarTemaSalvo } from "./tema.js";
import { TenantAuthSplit } from "@cert4all/ui";

/*
  O TEMA É PINTADO NA IMPORTAÇÃO, antes de qualquer componente montar.

  Ele mora aqui, e não num `<script>` inline no `index.html` de cada build, por uma razão só: seriam
  DUAS cópias da regra de resolver o tema (a inline e a do `tema.ts`), e a inline é justamente a que
  ninguém lembra de atualizar. Uma verdade sobre a cor, num lugar só.

  ⚠️ O preço, medido e aceito: entre o HTML chegar e este módulo carregar, a página fica no tema
  claro. Num SPA isso é **a tela em branco de sempre** — não há conteúdo pintado para piscar —, e
  quem tem o SO em escuro já convive com esse instante hoje. Se um dia o painel passar a renderizar
  no servidor, aí sim o `<head>` precisa resolver o tema antes.
*/
aplicarTemaSalvo();

/**
 * O cliente do TanStack Query, com as políticas que dependem do que este produto é.
 *
 * A mais importante é o `retry`: **só repete o que vale repetir**. Um `retry: 3` padrão faria o app
 * insistir três vezes num 403, o que não muda nada e atrasa a tela de "sem permissão" em segundos —
 * e insistir num 401 depois de a sessão ter morrido.
 */
export function criarClienteDeDados(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Trinta segundos: o inbox recebe evento por SSE (H30) e não deve depender de polling; o
        // painel mostra estado de canal, que muda em minutos, não em milissegundos.
        staleTime: 30_000,
        retry: (tentativas, erro) => {
          const falha = falhaDe(erro);
          return falha ? valeRepetir(falha) && tentativas < 2 : false;
        },
        // Refetch ao voltar para a aba é o que faz o estado de canal deixar de estar velho depois de
        // o atendente passar a manhã em outra janela.
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Mutação NUNCA repete sozinha. Repetir um "enviar mensagem" que talvez tenha saído produz
        // mensagem duplicada no telefone de um consumidor — e isso não se desfaz.
        retry: false,
      },
    },
  });
}

export interface AppProps {
  readonly children: ReactNode;
  /** O console liga; painel e inbox não — §5 regra 3 da spec 02. */
  readonly exigirPlataforma?: boolean;
  /** Para onde mandar quem não tem sessão. */
  readonly urlDeLogin: string;
  readonly cliente?: QueryClient;
}

/**
 * Onde a sessão não existe.
 *
 * As quatro fases levam a telas diferentes de propósito. Colapsá-las em "faça login" é o erro que o
 * H9 tomou trabalho para evitar do lado do servidor: num 503, o login depende do mesmo Cert4All que
 * está fora, então mandar a pessoa para lá é mandá-la para uma porta fechada.
 */
function SemSessao(props: { readonly estado: Exclude<EstadoDaSessao, { fase: "pronta" }>; readonly urlDeLogin: string }) {
  const { estado } = props;

  if (estado.fase === "carregando") {
    // Esqueleto, não spinner — e, sobretudo, **não o menu**. Menu antes da permissão pisca item que a
    // pessoa não pode ver (§5 regra 1).
    return (
      <main className="mx-auto max-w-lg">
        <EstadoCarregando linhas={2} rotulo={textos.estados.carregando} />
      </main>
    );
  }

  if (estado.fase === "indisponivel") {
    return (
      <main className="mx-auto max-w-lg">
        <EstadoErroRecuperavel falha={estado.falha} aoRepetir={() => window.location.reload()} />
      </main>
    );
  }

  if (estado.fase === "iam_nao_configurado") {
    // Só acontece em dev, e nomeá-lo poupa uma hora de procurar bug no front.
    return (
      <main role="alert" className="mx-auto max-w-lg px-6 py-12 text-center">
        <p className="text-base font-medium">{textos.estados.iamNaoConfigurado.titulo}</p>
        <p className="mt-2 text-sm text-[color:var(--tinta-fraca)]">
          {textos.estados.iamNaoConfigurado.corpo}
        </p>
        <code className="mt-3 block select-all rounded bg-[color:var(--vidro-controle)] px-2 py-1 text-xs">
          {estado.mensagem}
        </code>
      </main>
    );
  }

  if (estado.fase === "sem_plataforma") {
    /*
      Sessão válida, pessoa que não é da plataforma.

      **Sem botão de entrar**, e é o ponto: entrar de novo não muda nada, e oferecer o botão faria a pessoa
      tentar para sempre. O que ela precisa saber é que está no endereço errado — e o caminho de volta é o
      painel do cliente, não o login.
    */
    return (
      <main role="alert" className="mx-auto max-w-lg px-6 py-12 text-center">
        <p className="text-base font-medium">{textos.estados.semPlataforma.titulo}</p>
        <p className="mt-2 text-sm text-[color:var(--tinta-fraca)]">
          {textos.estados.semPlataforma.corpo}
        </p>
        <p className="mt-3 text-xs text-[color:var(--tinta-fraca)]">
          Entrando como {estado.nomeDoUsuario}.
        </p>
      </main>
    );
  }

  return <Entrar />;
}

/**
 * O formulário de entrada (H59).
 *
 * ## Por que ele existe, e o que havia antes
 *
 * Antes havia um botão que fazia `window.location.assign(urlDeLogin)`, e `urlDeLogin` vem de
 * `VITE_URL_DE_LOGIN` — que **sem configuração cai em `/`**, o próprio painel. A pessoa clicava,
 * voltava e lia "sua sessão terminou" outra vez, para sempre. O comentário do `sessao.tsx` já
 * descrevia esse laço como defeito; ele simplesmente nunca tinha sido fechado.
 *
 * ## A senha não fica no navegador
 *
 * O campo existe, o `POST /api/entrar` recebe, e o servidor troca por um cookie `HttpOnly`. Nada é
 * guardado em `localStorage` — o §11 da spec 02 proíbe token legível por JavaScript, e senha então nem
 * se discute. Sem `autoComplete` desligado: gerenciador de senhas é o caminho seguro, e atrapalhá-lo
 * empurra a pessoa para senha fraca e digitada à mão.
 *
 * ## A recusa mostra a frase do Cert4All
 *
 * "Conta desativada", "senha vencida" e "usuário ou senha inválidos" são coisas diferentes, e cada uma
 * pede uma ação diferente de quem lê. Achatar tudo em "não foi possível entrar" transformaria um
 * problema resolvível em mistério.
 */
/** Onde a senha se recupera: no Cert4All, com a marca do Hugi e a volta para ca'. */
const URL_DE_RECUPERACAO_DE_SENHA = "https://cert4all.com.br/forgot-password?product=hugi";

/**
 * A cor da marca para o painel de entrada, lida do PRÓPRIO token.
 *
 * ⚠️ Não é contorno da catraca da palheta (que proíbe hex em `.ts`/`.tsx`) — é o que ela quer.
 * A cor continua tendo uma fonte só, o `vidro.css`, e passa a seguir o modo escuro sozinha: quem
 * ajustar `--acao-forte` ajusta esta tela junto, sem saber que ela existe.
 *
 * Vazio (CSS ainda não aplicado, ou ambiente sem DOM) devolve `null`, e o componente cai no
 * próprio padrão dele em vez de pintar com string quebrada.
 */
/**
 * A telha da marca, servida de `web/ui/marca/publico` pelo `publicDir` dos três front.
 *
 * ⚠️ É a telha (fundo próprio + traços brancos), não o `icone-monocromo.svg`. O monocromo pinta com
 * `currentColor`, e `currentColor` NÃO é herdado por SVG carregado em `<img>` — ele sairia preto
 * sobre o azul do painel. A telha traz a própria cor e por isso lê em qualquer fundo.
 */
const MARCA_DO_HUGI = "/favicon.svg";

function corDaMarca(): string | null {
  if (typeof document === "undefined") return null;
  const lida = getComputedStyle(document.documentElement).getPropertyValue("--acao-forte").trim();
  return lida || null;
}

function Entrar() {
  // Inicializador de `useState`, e não corpo do componente: `consumirMarcaDeSaida` **apaga** a marca,
  // então rodá-la a cada render leria `true` uma vez e `false` no render seguinte — o título mudaria
  // sozinho debaixo de quem está digitando.
  const [saiu] = useState(consumirMarcaDeSaida);
  const cabecalho = saiu ? textos.estados.saiu : textos.estados.semSessao;

  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function submeter(evento: React.FormEvent) {
    evento.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const r = await fetch("/api/entrar", {
        method: "POST",
        // `same-origin` porque é aqui que o cookie de sessão é POSTO — sem isto o navegador
        // descartaria o `Set-Cookie` da resposta e o login "funcionaria" sem efeito nenhum.
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ usuario, senha }),
      });
      if (r.status === 204) {
        // Recarrega em vez de mexer em estado: o `PortaoDeSessao` já sabe buscar `/api/sessao`, e
        // duplicar essa lógica aqui criaria duas verdades sobre "estou logado".
        window.location.reload();
        return;
      }
      const corpo = (await r.json().catch(() => ({}))) as { mensagem?: string };
      setErro(corpo.mensagem ?? "Não foi possível entrar.");
    } catch {
      setErro("Não consegui falar com o servidor. Confira se ele está no ar.");
    } finally {
      setEnviando(false);
    }
  }

  /*
    A classe `campo` do `vidro.css`, e não um terceiro desenho à mão.

    Esta era a TERCEIRA maneira de escrever um campo no produto, e a de pior contraste: com
    `border-[color:var(--borda)]` — que é navy a **10%** — a borda dava **1,20:1** contra o fundo
    do campo, medido em produção. A WCAG 1.4.11 pede 3:1 para identificar um controle, e é aqui
    que ele mais importa: é a primeira tela que qualquer pessoa vê, e a única em que ela precisa
    saber onde digitar sem conhecer o produto.

    ⚠️ **E a medição errada dizia 12,38.** `--borda` tem alfa 0,1, e ler `borderTopColor` sem
    compor contra o fundo dá a cor CRUA — quase preta. Medida de cor com alfa que não compõe não
    é medida; é a mesma armadilha de 02/09, aqui na borda em vez de no texto.
  */
  const campo = "campo mt-1 w-full";

  /*
    SPLIT-SCREEN (17/09), do `@cert4all/ui` — o mesmo componente do Cert4All e do Stonen.

    ⚠️ A MARCA AQUI É A DO HUGI, não a do cliente. Esta tela é pré-autenticação: não há como saber
    de que empresa a pessoa é antes de ela digitar o e-mail.

    ⚠️ O TÍTULO CONTINUA SENDO O DE SAÍDA quando ela acabou de sair ("Sua sessão terminou"), e não
    um "bem-vindo" fixo. Era o comportamento de antes e ele é informativo: quem foi deslogado
    precisa saber que foi, senão tenta entender por que o app "voltou para o login".
  */
  return (
    <TenantAuthSplit
      companyName="Hugi"
      logoUrl={MARCA_DO_HUGI}
      primaryColor={corDaMarca()}
      formTitle={cabecalho.titulo}
      ownerSignature={{
        product: "Hugi",
        lead: "by",
        owner: "Cert4All",
        site: "cert4all.com.br",
        href: "https://cert4all.com.br",
      }}
      error={erro}
      pageFooter={
        <p>A sua conta é a do Cert4All. O Hugi não guarda senha — ele troca a sua credencial por uma sessão.</p>
      }
    >
      <p className="-mt-2 text-sm text-[color:var(--tinta-fraca)]">{cabecalho.corpo}</p>

      <form className="text-left" onSubmit={submeter}>
        <label className="block text-sm">
          E-mail ou usuário
          <input
            type="text"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            required
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            className={campo}
          />
        </label>

        <label className="mt-3 block text-sm">
          Senha
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            className={campo}
          />
        </label>

        {/*
          A senha e' do Cert4All, e e' la' que se recupera — mas quem esta' AQUI nao tem como chegar la'
          sem este link (owner, 03/09: "mas como ele reseta a senha?"). O `?product=hugi` da a marca do
          Hugi ao e-mail e faz a redefinicao voltar para app.hugi.com.br, nao para o login do Cert4All.
        */}
        <p className="mt-2 text-right text-xs">
          <a
            href={URL_DE_RECUPERACAO_DE_SENHA}
            className="text-[color:var(--acao-texto)] underline-offset-2 hover:underline"
          >
            Esqueceu a senha?
          </a>
        </p>

        {erro ? (
          <p role="alert" className="mt-3 text-sm text-[color:var(--erro-texto)]">
            {erro}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={enviando}
          className={[
            "mt-4 w-full rounded-md px-3 py-2 text-sm font-medium",
            "bg-[color:var(--acao-forte)] text-[color:var(--tinta-na-acao)]",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--acao)]",
            enviando ? "opacity-60" : "",
          ].join(" ")}
        >
          {enviando ? "Entrando…" : textos.acoes.entrar}
        </button>
      </form>
    </TenantAuthSplit>
  );
}

export function App(props: AppProps) {
  const cliente = props.cliente ?? criarClienteDeDados();

  return (
    <StrictMode>
      <QueryClientProvider client={cliente}>
        <PortaoDeSessao
          {...(props.exigirPlataforma ? { exigirPlataforma: true } : {})}
          /*
            O envelope do fundo mora AQUI, e não na `Casca` só.

            `fundo-do-produto` traz o `--fundo` **e** a `--tinta`, e até 07/09 ele era aplicado num
            lugar só: `casca.tsx`. A casca só existe **depois** da sessão — então as cinco telas que
            `SemSessao` desenha (entrada, carregando, indisponível, IAM não configurado, sem
            plataforma) rodavam com `html`, `body` e `main` transparentes: fundo BRANCO do navegador
            e a tinta preta que ele herda por padrão, com os componentes usando os tokens do tema.

            No claro isso passava despercebido — o branco do navegador é quase o cinza de app da
            palheta, e o preto herdado contrasta.
            **No escuro quebrava**, medido em produção na tela de entrada: a frase de apoio a
            **1,95**, o "Esqueceu a senha?" a **2,03**, o texto do campo a 4,13 e a borda dele a
            2,64 contra a página. É a PRIMEIRA tela que qualquer pessoa vê, e o link que quem
            esqueceu a senha precisa achar.

            `min-h-screen` e não `h-screen`: o caso "IAM não configurado" mostra a mensagem crua do
            servidor, e com altura fixa um texto longo seria cortado sem barra de rolagem — o mesmo
            defeito que o `<main>` da casca já pagou uma vez.

            ⚠️ E ele fica **só neste ramo**, não no `App` inteiro: a casca já traz a classe, e
            aninhá-las sobreporia os três gradientes radiais (que são `background-attachment: fixed`)
            — o fundo do produto logado mudaria de cor sem ninguém pedir.
          */
          aoNaoTerSessao={(estado) => (
            <div className="fundo-do-produto min-h-screen">
              <SemSessao estado={estado} urlDeLogin={props.urlDeLogin} />
            </div>
          )}
        >
          {props.children}
        </PortaoDeSessao>
      </QueryClientProvider>
    </StrictMode>
  );
}
